import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Gamepad2, Users, Play, Zap, Flame } from 'lucide-react'
import randomCodeGenerator from '../utils/randomCodeGenerator'
import logo from '../assets/logo-no-sub.png'
import blueChili from '../assets/blue-chili.png'
import magentaChili from '../assets/magenta-chili.png'
import '../components/css/homepage.css'

const Homepage = () => {
    const [roomCode, setRoomCode] = useState('')

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.8,
                staggerChildren: 0.2
            }
        }
    }

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    }

    const cardVariants = {
        hidden: { y: 50, opacity: 0, scale: 0.9 },
        visible: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        },
        hover: {
            y: -10,
            scale: 1.02,
            transition: {
                duration: 0.2
            }
        }
    }

    const featureCards = [
        { icon: Gamepad2, title: "Real-time Gameplay", desc: "Play with friends instantly" },
        { icon: Users, title: "Multiplayer", desc: "Up to 10 players per game" },
        { icon: Zap, title: "Fast-paced", desc: "Quick rounds, endless fun" }
    ]

    return (
        <div className="homepage-modern">
            <motion.div
                className="homepage-container"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Background Elements */}
                <div className="bg-elements">
                    <motion.div
                        className="bg-circle bg-circle-1"
                        style={{
                            backgroundImage: `url(${blueChili})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            opacity: 0.15,
                            filter: 'brightness(0) saturate(100%) invert(13%) sepia(100%) saturate(1000%) hue-rotate(340deg) brightness(95%) contrast(95%)',
                            mixBlendMode: 'multiply'
                        }}
                        animate={{
                            rotate: [-15, 15, -15],
                            scale: [1, 1.1, 1]
                        }}
                        transition={{
                            rotate: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                            scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                        }}
                    />
                    <motion.div
                        className="bg-circle bg-circle-2"
                        style={{
                            backgroundImage: `url(${magentaChili})`,
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            opacity: 0.15,
                            filter: 'brightness(0) saturate(100%) invert(13%) sepia(100%) saturate(1000%) hue-rotate(340deg) brightness(95%) contrast(95%)',
                            mixBlendMode: 'multiply'
                        }}
                        animate={{
                            rotate: -360,
                            scale: [1.2, 1, 1.2]
                        }}
                        transition={{
                            rotate: { duration: 25, repeat: Infinity, ease: "linear" },
                            scale: { duration: 6, repeat: Infinity, ease: "easeInOut" }
                        }}
                    />
                </div>

                {/* Header */}
                <div className="homepage-header">
                    <div className="logo-container">
                        <Flame className="flame-icon" />
                        <img src={logo} alt="Heated Logo" className="logo-image" />
                        <p className="game-subtitle">The Colorblind-Friendly Card Game Experience</p>
                    </div>
                </div>

                {/* Feature Cards */}
                <motion.div
                    className="features-grid"
                    variants={itemVariants}
                >
                    {featureCards.map((feature, index) => (
                        <motion.div
                            key={index}
                            className="feature-card card-modern"
                            variants={cardVariants}
                            whileHover="hover"
                        >
                            <feature.icon className="feature-icon" />
                            <h3>{feature.title}</h3>
                            <p>{feature.desc}</p>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Main Game Form */}
                <motion.div
                    className="game-form card-modern"
                    variants={cardVariants}
                    whileHover="hover"
                >
                    <motion.div variants={itemVariants}>
                        <h2>Ready to Play?</h2>
                        <p>Join an existing game or create a new one</p>
                    </motion.div>

                    <motion.div
                        className="form-section"
                        variants={itemVariants}
                    >
                        <div className="input-group">
                            <input
                                type='text'
                                placeholder='Enter Game Code'
                                value={roomCode}
                                onChange={(event) => setRoomCode(event.target.value)}
                                className="modern-input"
                            />
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Link to={`/playHeated?roomCode=${roomCode}`}>
                                    <button className="btn-modern btn-success" disabled={!roomCode.trim()}>
                                        <Users className="btn-icon" />
                                        Join Game
                                    </button>
                                </Link>
                            </motion.div>
                        </div>

                        <div className="divider">
                            <span>OR</span>
                        </div>

                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Link to={`/playHeated?roomCode=${randomCodeGenerator(5)}`}>
                                <button className="btn-modern btn-primary create-game-btn">
                                    <Play className="btn-icon" />
                                    Create New Game
                                </button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>

                {/* Footer */}
                <motion.footer
                    className="homepage-footer"
                    variants={itemVariants}
                >
                    {/* <p>Built with ❤️ for card game enthusiasts</p> */}
                </motion.footer>
            </motion.div>

        </div>
    )
}

export { Homepage }
